/**
 * Generates HTML email template for "Advance Invoice".
 * Sent to clients when an advance or additional services invoice is created.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter, icon } from '../../emailLayout';
import { trunc2 } from '@/utils';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * @param {Object}  opts
 * @param {string}  opts.clientName
 * @param {number}  opts.itemCost              - subtotal (sum of all service amounts, incl. per-service tax)
 * @param {number}  opts.total                 - grand total due
 * @param {number}  [opts.trustjetFee]         - TrustJet platform fee amount (rendered when NOT oneOff)
 * @param {string}  [opts.quoteNumber]
 * @param {Array}   [opts.services]      - [{ item, amount, tax }]
 * @param {Object}  [opts.organization]
 */
export const buildInvoicePaymentHoldEmailHtml = ({
  clientName,
  itemCost,
  total,
  quoteNumber,
  trustjetFee = 0,
  taxAmount,
  services,
  cardFee = 0,
  organization,
  last4 = null
}) => {

  const totalCardFee = trunc2(total * 0.03);
  const totalWithCardFee = trunc2(total + totalCardFee);

  // ── Per-service rows ─────────────────────────────────────────────────────
  // Each service renders as a gray-background block (sub-table) with a white
  // gap between groups, matching the invoice details mockup layout.
  const serviceRowsHtml = (() => {
    if (services?.length > 0) {
      return services.map((s, i) => {
        const isLast = i === services.length - 1;
        return `
              <tr>
                <td colspan="2" style="padding:0 20px ${isLast ? '0' : '6px'} 20px;">
                  <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td style="padding:12px 20px 4px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">${s.item || s.name || ''}</td>
                      <td align="right" style="padding:12px 20px 4px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">$${fmt(s.amount)}</td>
                    </tr>
                  </table>
                </td>
              </tr>`;
      }).join('');
    }
    return '';
  })();


  return `
${emailDocOpen({ title: 'Payment Hold Released' })}
${emailHeader({ title: 'Payment Hold Released', organization })}

        ${spacer()}

        <!-- ===== CLIENT / REFERENCE ROW ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice For</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${clientName || 'Valued Client'}</span>
                  </td>
                  ${quoteNumber ? `<td align="right">
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">TR Number</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${quoteNumber}</span>
                  </td>` : ''}
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== NOTIFICATION CARD ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:28px 28px 32px;">
              <!-- Icon -->
              <table role="presentation" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
                <tr>
                  <td style="width:48px;height:48px;text-align:center;vertical-align:middle;background-color:#f9ebec;border-radius:100%;">
                    ${icon('bill', 22)}
                    </td>
                </tr>
              </table>
              <!-- Body text -->
              <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
                The temporary reservation hold on your card ending in <strong style="color:#111827;">${last4}</strong> has expired because the requested additional services were not finalized within the operator's clearance window.
              </p>
              <p>The held amount of <strong style="color:#111827;">USD ${totalWithCardFee}</strong> has been released automatically and will return to your available credit balance within 1-7 business days, depending on your bank. No active charge has been made to your card, and the flight is currently unconfirmed.</p>
              <p>To re-initiate this request or for any other questions, please reach out to <strong style="color:#111827;">${organization?.name}</strong> or contact your broker directly.</p>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== INVOICE DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <!-- Section label -->
          <tr>
            <td colspan="2" style="padding:14px 20px 8px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice Details</span>
            </td>
          </tr>

          <!-- Service rows -->
          ${serviceRowsHtml}

          <!-- Subtotal -->
          <tr>
            <td style="padding:10px 20px 4px;font-size:13px;color:#6B7280;">Subtotal Cost</td>
            <td align="right" style="padding:10px 20px 4px;font-size:13px;color:#6B7280;">$${fmt(itemCost)}</td>
          </tr>

          <!-- Taxes and Fees -->
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Taxes and Fees</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">$${fmt(trunc2(Number(taxAmount || 0) + Number(trustjetFee || 0)))}</td>
          </tr>

          <!-- Divider before total -->
          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>

          <!-- Total Due -->
          <tr>
            <td style="padding:14px 20px;font-size:15px;font-weight:700;color:#111827;">Total Due</td>
            <td align="right" style="padding:14px 20px;font-size:18px;font-weight:700;color:#111827;">$${fmt(cardFee && cardFee > 0 ? totalWithCardFee : total)}</td>
          </tr>

          ${cardFee && cardFee > 0 ? `
          
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Credit/Debit Card Fee (3%)</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">$${totalCardFee.toFixed(2)}</td>
          </tr>

          ` : ``}

        </table>

        ${spacer()}

${emailFooter()}
${emailDocClose()}`;
};
